// Entry-point Vite + Solid + @solidjs/router (sans Vinxi/SolidStart).
//
// Routes définies manuellement (pas file-based) car Vite vanilla n'a pas
// file-routing natif. C'est OK pour un nombre limité de routes.
// Pour les 22 panneaux d'opérateur on délégue à `<App>` legacy qui les
// gère déjà via PANEL_REGISTRY.

import { render } from "solid-js/web";
import { Router, Route, useNavigate } from "@solidjs/router";
import { onMount } from "solid-js";
import { QueryClient, QueryClientProvider } from "@tanstack/solid-query";

import HomePage from "./routes/index";
import LoginPage from "./routes/login";
import ConsoleLayout from "./routes/console";
import ConsolePluginsRoute from "./routes/console-plugins";
import ConsoleClientsRoute from "./routes/console-clients";
import ConsoleCliClientsRoute from "./routes/console-cli-clients";
import ConsoleAuditRoute from "./routes/console-audit";
import ConsoleBugReportsRoute from "./routes/console-bug-reports";
import ConsoleSessionsListRoute from "./routes/console-sessions-list";
import ConsoleSettingsRoute from "./routes/console-settings";
import ConsoleSessionPage from "./routes/console-session";
import ShareReceivePage from "./routes/share-receive";
import { refreshRuntimeConfig } from "../operator/shared/runtime-config";

import ConsoleSuperAdminLayout from "./routes/console-super-admin";
import ConsoleSuperAdminDashboard from "./routes/console-super-admin-dashboard";
import ConsoleSuperAdminTenants from "./routes/console-super-admin-tenants";
import ConsoleSuperAdminUsers from "./routes/console-super-admin-users";
import ConsoleSuperAdminPlugins from "./routes/console-super-admin-plugins";
import ConsoleSuperAdminSignalements from "./routes/console-super-admin-signalements";
import ConsoleSuperAdminDbMetrics from "./routes/console-super-admin-db-metrics";
import ConsoleSuperAdminClientsSessions from "./routes/console-super-admin-clients-sessions";
import ConsoleSuperAdminCliClients from "./routes/console-super-admin-cli-clients";
import ConsoleSuperAdminRelays from "./routes/console-super-admin-relays";
import ConsoleSuperAdminTrace from "./routes/console-super-admin-trace";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 10_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root introuvable");

// Redirect /console (index) vers le cockpit. Les sous-routes /console/*
// (clients, plugins, audit, sessions, settings, super-admin) restent
// accessibles. La page RMM tabulaire est désormais un onglet workspace du
// cockpit, ouvert via le logo "R".
function RedirectCockpit() {
  const navigate = useNavigate();
  onMount(() => navigate("/console/session", { replace: true }));
  return null;
}

function installConsoleBuildUpdatePrompt() {
  if (!location.pathname.startsWith("/console")) return;

  const initialBundle = (() => {
    for (const script of Array.from(document.scripts)) {
      const match = script.src.match(/\/dist-start\/console\.([A-Za-z0-9_-]+)\.js/);
      if (match) return match[1];
    }
    return null;
  })();
  if (!initialBundle) return;

  let shown = false;
  const showPrompt = (nextBundle: string) => {
    if (shown || document.getElementById("rws-build-update-prompt")) return;
    shown = true;
    console.log("[DEBUG:build] new version prompt shown", { initialBundle, nextBundle });

    const box = document.createElement("div");
    box.id = "rws-build-update-prompt";
    box.setAttribute("role", "status");
    box.style.cssText = [
      "position:fixed",
      "right:16px",
      "bottom:16px",
      "z-index:2147483647",
      "display:flex",
      "align-items:center",
      "gap:10px",
      "max-width:min(420px,calc(100vw - 32px))",
      "padding:12px 14px",
      "border:1px solid rgba(148,163,184,.45)",
      "border-radius:8px",
      "background:#0f172a",
      "color:#f8fafc",
      "box-shadow:0 18px 45px rgba(15,23,42,.32)",
      "font:13px/1.35 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    ].join(";");

    const text = document.createElement("div");
    text.style.cssText = "min-width:0;flex:1";
    text.textContent = `Nouvelle version disponible (${initialBundle} -> ${nextBundle}).`;

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "Recharger";
    button.style.cssText = [
      "flex:0 0 auto",
      "border:1px solid rgba(248,250,252,.35)",
      "border-radius:6px",
      "background:#f8fafc",
      "color:#0f172a",
      "padding:7px 10px",
      "font:600 13px system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "cursor:pointer",
    ].join(";");
    button.addEventListener("click", () => window.location.reload());

    box.append(text, button);
    document.body.append(box);
  };

  const check = async () => {
    try {
      const response = await fetch("/api/version", { cache: "no-store", credentials: "same-origin" });
      if (!response.ok) return;
      const data = (await response.json()) as { bundle?: string };
      if (data.bundle && data.bundle !== initialBundle) showPrompt(data.bundle);
    } catch {
      /* network down: keep the current session quiet */
    }
  };

  window.addEventListener("load", () => {
    void check();
    window.setInterval(() => void check(), 15_000);
  });
}

installConsoleBuildUpdatePrompt();

// PWA — enregistre le service worker (offline shell + cache assets hashés).
// Échoue silencieusement si non supporté (HTTP non-localhost, navigateur ancien).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => console.warn("[pwa] enregistrement du service worker échoué", err));
  });
}

// La console /console utilise ce bundle. Enregistrer aussi le service worker
// ici, sinon seuls les anciens entrypoints le mettent à jour.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => registration.update().catch(() => undefined))
      .catch((err) => console.warn("[pwa] enregistrement du service worker échoué", err));
  });
}

void refreshRuntimeConfig().finally(() => {
  render(
    () => (
      <QueryClientProvider client={queryClient}>
        <Router>
          <Route path="/" component={HomePage} />
          <Route path="/login" component={LoginPage} />
          <Route path="/console" component={ConsoleLayout}>
            <Route path="/" component={RedirectCockpit} />
            <Route path="/plugins" component={ConsolePluginsRoute} />
            <Route path="/clients" component={ConsoleClientsRoute} />
            <Route path="/cli" component={ConsoleCliClientsRoute} />
            <Route path="/audit" component={ConsoleAuditRoute} />
            <Route path="/bug-reports" component={ConsoleBugReportsRoute} />
            <Route path="/sessions" component={ConsoleSessionsListRoute} />
            <Route path="/settings" component={ConsoleSettingsRoute} />
          </Route>
          <Route path="/console/super-admin" component={ConsoleSuperAdminLayout}>
            <Route path="/" component={ConsoleSuperAdminDashboard} />
            <Route path="/tenants" component={ConsoleSuperAdminTenants} />
            <Route path="/users" component={ConsoleSuperAdminUsers} />
            <Route path="/plugins" component={ConsoleSuperAdminPlugins} />
            <Route path="/signalements" component={ConsoleSuperAdminSignalements} />
            <Route path="/db-metrics" component={ConsoleSuperAdminDbMetrics} />
            <Route path="/clients-sessions" component={ConsoleSuperAdminClientsSessions} />
            <Route path="/cli-clients" component={ConsoleSuperAdminCliClients} />
            <Route path="/relays" component={ConsoleSuperAdminRelays} />
            <Route path="/trace" component={ConsoleSuperAdminTrace} />
          </Route>
          <Route path="/console/session" component={ConsoleSessionPage} />
          <Route path="/share-receive" component={ShareReceivePage} />
        </Router>
      </QueryClientProvider>
    ),
    root,
  );
});
